/**
 * SummarySubTab Component
 *
 * Financial summary sub-tab showing:
 * - 12-Month Forecast Chart
 * - Cost Summary (CapEx, OpEx, Total by type)
 * - Benefits Summary (by category)
 * - ROI Metrics (Net Benefit, ROI %, Payback Period, NPV)
 */

import {
  useCostSummary,
  useBenefitSummary,
  useForecast,
  useGenerateForecast,
} from '@medsuite/api-client';
import { Card, Button } from '@medsuite/ui-components';
import React from 'react';

import { MonthlyForecastChart } from './MonthlyForecastChart';

import type { Investment } from '@medsuite/models';

interface SummarySubTabProps {
  investment: Investment;
}

/**
 * Format currency helper
 */
const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

/**
 * Format percentage helper
 */
const formatPercent = (value: number): string => {
  return `${value.toFixed(1)}%`;
};

/**
 * SummarySubTab Component
 */
export const SummarySubTab: React.FC<SummarySubTabProps> = ({ investment }) => {
  const investmentId = investment.investment_id;

  // Fetch summaries
  const { data: costSummary, isLoading: costsLoading } = useCostSummary(investmentId);
  const { data: benefitSummary, isLoading: benefitsLoading } = useBenefitSummary(investmentId);
  const {
    data: forecast,
    isLoading: forecastLoading,
    error: forecastError,
  } = useForecast(investmentId);

  // Generate forecast mutation
  const generateForecast = useGenerateForecast(investmentId);

  const isLoading = costsLoading || benefitsLoading;

  // Use forecast data if available, fall back to simple summaries
  const totalCosts = forecast?.totals?.total_cost || costSummary?.grand_total || 0;
  const totalBenefits =
    forecast?.totals?.total_benefit || benefitSummary?.total_monetary_benefit || 0;
  const netBenefit = forecast?.totals?.net_benefit ?? totalBenefits - totalCosts;
  const roiPercent =
    forecast?.totals?.roi_percentage ?? (totalCosts > 0 ? (netBenefit / totalCosts) * 100 : 0);
  const paybackMonth = forecast?.totals?.payback_month;
  const npv = forecast?.totals?.npv;

  // Handle generate forecast
  const handleGenerateForecast = () => {
    generateForecast.mutate(undefined);
  };

  if (isLoading) {
    return (
      <div className="p-6 text-center text-gray-500 dark:text-gray-400">
        Loading financial summary...
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* 12-Month Forecast Section */}
      <Card>
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              12-Month Financial Forecast
            </h3>
            <Button
              variant="secondary"
              size="sm"
              onClick={handleGenerateForecast}
              loading={generateForecast.isPending}
            >
              {forecast?.months?.length ? 'Regenerate' : 'Generate'} Forecast
            </Button>
          </div>

          {forecastError && (
            <div className="mb-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg text-sm text-yellow-700 dark:text-yellow-300">
              Forecast not available. Add costs and benefits, then click &quot;Generate Forecast&quot; to see
              projections.
            </div>
          )}

          <MonthlyForecastChart
            months={forecast?.months || []}
            totals={
              forecast?.totals || {
                total_cost: totalCosts,
                total_benefit: totalBenefits,
                net_benefit: netBenefit,
                roi_percentage: roiPercent,
                payback_month: null,
                npv: null,
              }
            }
            isLoading={forecastLoading || generateForecast.isPending}
          />
        </div>
      </Card>

      {/* Summary Cards Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Cost Summary Card */}
        <Card>
          <div className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Cost Summary (Year 1)
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600 dark:text-gray-400">Total CapEx:</span>
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {formatCurrency(costSummary?.total_capex || 0)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600 dark:text-gray-400">Total OpEx:</span>
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {formatCurrency(costSummary?.total_opex || 0)}
                </span>
              </div>
              <div className="border-t border-gray-200 dark:border-gray-700 pt-3 mt-3">
                <div className="flex justify-between items-center">
                  <span className="text-base font-semibold text-gray-900 dark:text-gray-100">
                    Grand Total:
                  </span>
                  <span className="text-base font-bold text-red-600">
                    {formatCurrency(totalCosts)}
                  </span>
                </div>
              </div>
            </div>

            {/* Breakdown by category */}
            {costSummary && costSummary.by_category && costSummary.by_category.length > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  By Category:
                </h4>
                <div className="space-y-2">
                  {costSummary.by_category.map((cat) => (
                    <div key={cat.cost_category_id} className="flex justify-between text-xs">
                      <span className="text-gray-600 dark:text-gray-400">
                        {cat.cost_category_name}
                      </span>
                      <span className="text-gray-900 dark:text-gray-100 font-medium">
                        {formatCurrency(cat.total)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </Card>

        {/* Benefits Summary Card */}
        <Card>
          <div className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Benefits Summary (Year 1)
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  Quantified Benefits:
                </span>
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {benefitSummary?.type1_count || 0} items
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  Qualitative Benefits:
                </span>
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {benefitSummary?.type2_count || 0} items
                </span>
              </div>
              <div className="border-t border-gray-200 dark:border-gray-700 pt-3 mt-3">
                <div className="flex justify-between items-center">
                  <span className="text-base font-semibold text-gray-900 dark:text-gray-100">
                    Total Quantified:
                  </span>
                  <span className="text-base font-bold text-green-600">
                    {formatCurrency(totalBenefits)}
                  </span>
                </div>
              </div>
            </div>

            {/* Note about qualitative benefits */}
            {benefitSummary && benefitSummary.type2_count > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <p className="text-xs text-gray-500 dark:text-gray-400 italic">
                  * Qualitative benefits provide strategic value but are not included in monetary
                  calculations
                </p>
              </div>
            )}
          </div>
        </Card>

        {/* ROI Metrics Card */}
        <Card>
          <div className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              ROI Metrics
            </h3>
            <div className="space-y-4">
              <div>
                <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Net Benefit</div>
                <div
                  className={`text-2xl font-bold ${
                    netBenefit >= 0 ? 'text-green-600' : 'text-red-600'
                  }`}
                >
                  {formatCurrency(netBenefit)}
                </div>
              </div>

              <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">ROI (Year 1)</div>
                <div
                  className={`text-2xl font-bold ${
                    (roiPercent || 0) >= 0 ? 'text-green-600' : 'text-red-600'
                  }`}
                >
                  {roiPercent !== null ? formatPercent(roiPercent) : 'N/A'}
                </div>
              </div>

              <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Payback Period</div>
                <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                  {paybackMonth !== null && paybackMonth !== undefined
                    ? `Month ${paybackMonth}`
                    : 'N/A'}
                </div>
              </div>

              {npv !== null && npv !== undefined && (
                <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                  <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                    NPV (10% discount)
                  </div>
                  <div
                    className={`text-xl font-bold ${npv >= 0 ? 'text-green-600' : 'text-red-600'}`}
                  >
                    {formatCurrency(npv)}
                  </div>
                </div>
              )}
            </div>
          </div>
        </Card>
      </div>

      {/* Investment Health Indicator */}
      <Card>
        <div className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
            Investment Health
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">Total Investment</div>
              <div className="text-xl font-bold text-gray-900 dark:text-gray-100">
                {formatCurrency(totalCosts)}
              </div>
            </div>
            <div className="text-center">
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">Expected Returns</div>
              <div className="text-xl font-bold text-green-600">
                {formatCurrency(totalBenefits)}
              </div>
            </div>
            <div className="text-center">
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">Net Position</div>
              <div
                className={`text-xl font-bold ${
                  netBenefit >= 0 ? 'text-green-600' : 'text-red-600'
                }`}
              >
                {formatCurrency(netBenefit)}
              </div>
            </div>
            <div className="text-center">
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">Breakeven</div>
              <div className="text-xl font-bold text-purple-600 dark:text-purple-400">
                {paybackMonth !== null && paybackMonth !== undefined
                  ? `Month ${paybackMonth}`
                  : 'TBD'}
              </div>
            </div>
          </div>

          {/* Health indicator */}
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-center">
              {(roiPercent || 0) >= 50 && (
                <div className="flex items-center gap-2 text-green-600">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="font-medium">
                    Excellent Investment - High ROI with strong returns
                  </span>
                </div>
              )}
              {(roiPercent || 0) >= 20 && (roiPercent || 0) < 50 && (
                <div className="flex items-center gap-2 text-blue-600">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="font-medium">Good Investment - Positive returns expected</span>
                </div>
              )}
              {(roiPercent || 0) >= 0 && (roiPercent || 0) < 20 && (
                <div className="flex items-center gap-2 text-yellow-600">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="font-medium">
                    Marginal Investment - Consider qualitative benefits
                  </span>
                </div>
              )}
              {(roiPercent || 0) < 0 && (
                <div className="flex items-center gap-2 text-red-600">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="font-medium">
                    Negative ROI - Review cost structure and benefits
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default SummarySubTab;
